// The v4 identity grammar and generator live in `@lingjingai/screenplay/schema`.
// Only the wrappers that raise scriptctl's own CliError stay here.
import { CliError, EXIT_INPUT } from "../../../common.js";
import {
  ID_SPACE_EXHAUSTED,
  generateV4Id as generate,
  generateV4Ids as generateMany,
  isV4Id,
  V4_ID_PREFIXES,
  type V4IdByKind,
  type V4IdKind,
} from "@lingjingai/screenplay/schema";

export {
  isV4Id,
  V4_ID_ALPHABET,
  V4_ID_PATTERNS,
  V4_ID_PREFIXES,
  V4_ID_RANDOM_LENGTH,
  type V4IdByKind,
  type V4IdKind,
} from "@lingjingai/screenplay/schema";

export function requireV4Id<K extends V4IdKind>(kind: K, value: unknown, path: string): V4IdByKind[K] {
  if (isV4Id(kind, value)) return value;
  throw new CliError(`${path} must be ${V4_ID_PREFIXES[kind]} plus six Base58 characters.`, {
          exitCode: EXIT_INPUT,
          errorCode: "SCRIPT_V4_ID_INVALID",
          data: { expected: [`${V4_ID_PREFIXES[kind]} + 6 Bitcoin Base58 characters`], actual: [String(value)] },
        });
}

export function generateV4Id<K extends V4IdKind>(
  kind: K,
  existing: ReadonlySet<string> = new Set(),
  generateSuffix?: () => string,
): V4IdByKind[K] {
  try {
    return generateSuffix ? generate(kind, existing, generateSuffix) : generate(kind, existing);
  } catch (error) {
    // Only remap the one failure this is about; anything else keeps its own
    // identity instead of being relabelled as a retry exhaustion.
    if ((error as { code?: string })?.code !== ID_SPACE_EXHAUSTED) throw error;
    throw new CliError(`Could not generate an unused ${kind} ID.`, {
            exitCode: EXIT_INPUT,
            errorCode: "ID_SPACE_RETRY_EXHAUSTED",
            hint: "Retry the command. If this persists, inspect the configured ID generator.",
          });
  }
}

export function generateV4Ids<K extends V4IdKind>(
  kind: K,
  count: number,
  existing: ReadonlySet<string> = new Set(),
): V4IdByKind[K][] {
  if (!Number.isSafeInteger(count) || count < 1) {
    throw new CliError("count must be a positive integer.", {
            exitCode: EXIT_INPUT,
            errorCode: "ID_COUNT_INVALID",
            data: { actual: [String(count)] },
          });
  }
  return generateMany(kind, count, existing);
}
